'use client';

import { useTranslation } from 'react-i18next';
import { tx, useMediaSource, type MediaItem } from '@akademi/site-builder-runtime';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { ContentRail } from '../ui/ContentRail';
import { useAsyncResource } from '../_shared/useAsyncResource';

/**
 * Top-N portrait posters with oversized italic rank numerals
 * overlaid to the left of each poster. K-OTT signature pattern.
 * Backed by {@code MediaSource.videos.fetchSource(filter)}.
 */
export interface RankedShowcaseProps {
  title?: unknown;
  subtitle?: unknown;
  filter?: string;
  limit?: number;
  videoHrefTemplate?: string;
  seriesHrefTemplate?: string;
}

export function RankedShowcase({
  title: authoredTitle,
  subtitle,
  filter = 'weekly',
  limit = 10,
  videoHrefTemplate = '/tube/{slug}',
  seriesHrefTemplate = '/tube/series/{slug}',
}: RankedShowcaseProps) {
  const { t } = useTranslation('site-builder-blocks');
  const title = authoredTitle ?? t('rankedShowcase.title');
  const media = useMediaSource();
  const cap = Math.max(3, Math.min(limit, 20));

  const state = useAsyncResource<MediaItem[]>(
    async (signal) => {
      if (!media) return null;
      const res = await media.videos.fetchSource({ source: filter, limit: cap, signal });
      return res && res.items.length > 0 ? res.items : null;
    },
    [media, filter, cap],
  );

  const titleStr = tx(title) || t('rankedShowcase.titleShort');
  const subtitleStr = tx(subtitle);

  if (!media) {
    return (
      <SectionShell title={titleStr}>
        <AdapterPlaceholder blockType="RankedShowcase" adapterName="MediaSource" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={titleStr}>
        {subtitleStr && (
          <p
            style={{
              marginTop: -8,
              marginBottom: 12,
              color: 'var(--color-text-secondary, #71717a)',
            }}
          >
            {subtitleStr}
          </p>
        )}
        <SkeletonRow count={cap} layout="rail" aspect="2 / 3" minCardWidth={200} />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') {
    return (
      <SectionShell title={titleStr}>
        <EmptyState
          title={t('rankedShowcase.empty.title')}
          hint={t('rankedShowcase.empty.hint')}
          diagnostic={filter}
        />
      </SectionShell>
    );
  }

  return (
    <SectionShell title={titleStr}>
      {subtitleStr && (
        <p
          style={{ marginTop: -8, marginBottom: 12, color: 'var(--color-text-secondary, #71717a)' }}
        >
          {subtitleStr}
        </p>
      )}
      <ContentRail
        ariaLabel={titleStr || t('rankedShowcase.ariaLabel')}
        itemWidth="auto"
        gap="clamp(20px, 3vw, 32px)"
        padding="24px 0 8px"
      >
        {state.data.map((item, i) => {
          const rank = i + 1;
          const numeralGutter = rank >= 10 ? 'clamp(80px, 10vw, 110px)' : 'clamp(60px, 8vw, 80px)';
          const posterWidth = 'clamp(140px, 13vw, 200px)';
          const template = item.kind === 'series' ? seriesHrefTemplate : videoHrefTemplate;
          const href = template.replace('{slug}', encodeURIComponent(item.slug));
          return (
            <a
              key={item.id}
              href={href}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-end',
                textDecoration: 'none',
                color: 'inherit',
                paddingLeft: numeralGutter,
              }}
            >
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  left: 0,
                  bottom: -12,
                  fontSize: 'clamp(120px, 14vw, 200px)',
                  fontWeight: 900,
                  fontStyle: 'italic',
                  lineHeight: 0.85,
                  fontFamily: 'var(--font-family-display, inherit)',
                  letterSpacing: 'var(--font-tracking-tight, -0.06em)',
                  color: 'transparent',
                  WebkitTextStroke: '2px var(--color-brand-500, #ff2f6e)',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                {rank}
              </span>
              <div
                style={{
                  position: 'relative',
                  width: posterWidth,
                  aspectRatio: '2 / 3',
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: item.imageUrl
                    ? `center / cover no-repeat url("${item.imageUrl}")`
                    : 'linear-gradient(135deg, var(--color-brand-500, #ff2f6e), var(--color-text-primary, #18181b))',
                  boxShadow: '0 4px 12px var(--color-shadow, rgba(0,0,0,0.4))',
                  transition: 'transform 220ms ease',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'var(--color-brand-500, #ff2f6e)',
                    color: 'var(--color-text-on-brand, #fff)',
                    fontSize: '11px',
                    fontWeight: 800,
                    padding: 'var(--space-1, 4px) var(--space-2, 8px)',
                    borderRadius: 3,
                    letterSpacing: 'var(--font-tracking-wider, 0.04em)',
                  }}
                >
                  #{rank}
                </div>
              </div>
              <div
                style={{
                  position: 'absolute',
                  bottom: -56,
                  left: numeralGutter,
                  width: posterWidth,
                }}
              >
                <div
                  style={{
                    fontSize: 'clamp(12px, 1.5vw, 13px)',
                    fontWeight: 700,
                    color: 'var(--color-text-primary, #fff)',
                    lineHeight: 1.3,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical' as const,
                    overflow: 'hidden',
                  }}
                >
                  {item.title}
                </div>
              </div>
            </a>
          );
        })}
      </ContentRail>
      <div style={{ height: 56 }} />
    </SectionShell>
  );
}
